import { useCallback, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"

/**
 * Headless assistant-chat engine: owns the reusable lifecycle — thread
 * state, the "thinking" flag, the revert history stack, and error routing — and
 * delegates every domain-specific step to the injected `controller`. No formik,
 * fetching, or domain imports here, so this is safe to reuse for any dialog-embedded
 * assistant.
 *
 * @param {AssistantController} controller
 */
export const useAssistant = (controller) => {
  const [history, setHistory] = useState([])
  const [messages, setMessages] = useState([])
  const [isThinking, setIsThinking] = useState(false)

  const isSubmittingRef = useRef(false)

  const pushMessage = useCallback((role, content, exchangeId) => {
    if (!content) {
      return
    }

    const message = { id: uuidv4(), exchangeId, role, content }
    setMessages((prevMessages) => [...prevMessages, message])
  }, [])

  const onSubmit = useCallback(
    async (prompt) => {
      if (!prompt || isSubmittingRef.current) {
        return
      }

      isSubmittingRef.current = true
      setIsThinking(true)

      // One id for the whole exchange, stamped on every message it produces, so Revert
      // can drop them without keeping a parallel list of message ids.
      const exchangeId = uuidv4()

      pushMessage("user", prompt, exchangeId)

      try {
        const { message, result } = await controller.generate({
          prompt,
          messages
        })

        const snapshot = controller.snapshot()

        await controller.apply(result)

        pushMessage("assistant", message, exchangeId)

        const historyEntry = { exchangeId, snapshot, prompt }
        setHistory((prevHistory) => [...prevHistory, historyEntry])
      } catch (error) {
        pushMessage("error", error?.message || String(error), exchangeId)
      } finally {
        setIsThinking(false)
        isSubmittingRef.current = false
      }
    },
    [controller, messages, pushMessage]
  )

  /**
   * @returns {string|undefined} the reverted prompt's text, so the caller (the
   *   presentational panel) can prefill its input with it — or undefined if there
   *   was nothing to revert.
   */
  const onRevert = useCallback(() => {
    const entry = history[history.length - 1]

    if (!entry) {
      return undefined
    }

    setHistory((prevHistory) => prevHistory.slice(0, -1))
    controller.restore(entry.snapshot)
    controller.reportRevert?.()

    setMessages((prevMessages) =>
      prevMessages.filter((message) => message.exchangeId !== entry.exchangeId)
    )

    return entry.prompt
  }, [controller, history])

  return {
    messages,
    isThinking,
    canRevert: history.length > 0,
    onSubmit,
    onRevert
  }
}
